import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import type { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'

/**
 * Firelight stXRP (Flare, chain 14) — intrinsic APR from the on-chain
 * ERC-4626 exchange rate.
 *
 * stXRP is a non-rebasing ERC-4626 vault share over FXRP (6-dec / 6-dec).
 * Yield = coverage premiums streamed into the vault, which compound into
 * the share/asset rate (`convertToAssets`). There is no public APY API yet
 * ("developer portal … on the roadmap"), so the APR is recovered from the
 * *growth* of the rate over a fixed lookback window, annualised:
 *
 *   apr = (rateNow / rateThen − 1) × (yearSeconds / elapsedSeconds)
 *
 * Unlike stCELO this needs NO archival RPC: the vault stores its own
 * `totalAssetsAt(uint48)` / `totalSupplyAt(uint48)` checkpoint history
 * (pushed on every transfer / deposit / redeem), so the historical leg is
 * read at the LATEST block.
 *
 * Honest-by-construction caveat: coverage premiums only began streaming in
 * July 2026, so the measured rate has barely moved and the APR is ~0 % as
 * of 2026-09-17. This is a *measured trailing* rate, not a *set* rate — it
 * rises as premiums compound. A slash (rateNow < rateThen) throws, so
 * `safeFetch` surfaces 0 rather than a negative APR (a slash is a principal
 * loss, not negative yield).
 *
 * Key = the canonical token-list `assetGroup` `Firelight stXRP::stXRP` —
 * stXRP carries its OWN group (distinct from FXRP's `FXRP::FXRP`), so its
 * intrinsic yield is scoped to stXRP and never leaks into plain FXRP markets
 * that share no yield. The registry entry's `yieldKey` reads the same key.
 */

const CHAIN_ID = '14'
const VAULT: Address = '0x4c18ff3c89632c3dd62e796c0afa5c07c4c1b2b3'

/** 1e18 — the 1e18-scaled exchange rate is the canonical pricing primitive. */
const ONE_E18 = 10n ** 18n

/** Lookback window — 7 days, matching the stCELO rate-delta window. */
const WINDOW_SECONDS = 7 * 24 * 60 * 60

const VAULT_ABI = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'shares' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'totalAssetsAt',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint48', name: 'timestamp' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'totalSupplyAt',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint48', name: 'timestamp' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const computeApr = async (): Promise<number> => {
  const client = getEvmClient(CHAIN_ID)
  const head = await client.getBlockNumber()
  const headBlock = await client.getBlock({ blockNumber: head })
  const then = Number(headBlock.timestamp) - WINDOW_SECONDS

  // rateNow via the canonical 1e18-scaled getter; rateThen rebuilt from the
  // vault's own checkpoint history (1e18-scaled, latest-block read).
  const [rateNow, assetsThen, supplyThen] = await Promise.all([
    client.readContract({
      address: VAULT,
      abi: VAULT_ABI,
      functionName: 'convertToAssets',
      args: [ONE_E18],
    }) as Promise<bigint>,
    client.readContract({
      address: VAULT,
      abi: VAULT_ABI,
      functionName: 'totalAssetsAt',
      args: [then],
    }) as Promise<bigint>,
    client.readContract({
      address: VAULT,
      abi: VAULT_ABI,
      functionName: 'totalSupplyAt',
      args: [then],
    }) as Promise<bigint>,
  ])

  if (supplyThen === 0n) throw new Error('stXRP: zero historical totalSupply')
  const rateThen = (assetsThen * ONE_E18) / supplyThen

  // Sanity: both rates real (≥ 1e18) and non-decreasing. A slash makes
  // rateNow < rateThen — surface 0 via safeFetch, never a negative APR.
  if (rateNow < ONE_E18 || rateThen < ONE_E18 || rateNow < rateThen) {
    throw new Error(`stXRP: invalid rates (now=${rateNow}, then=${rateThen})`)
  }

  const apr = annualizeRateDeltaPercent(rateNow, rateThen, WINDOW_SECONDS)
  if (!Number.isFinite(apr) || apr < 0) {
    throw new Error(`stXRP: bad apr ${apr}`)
  }
  return apr
}

export const stxrpFetcher: YieldFetcher = {
  label: 'STXRP',
  fetch: async () => ({ 'Firelight stXRP::stXRP': await computeApr() }),
}
